/**
 * magneticPendulumPipeline — Magnetic pendulum chaos.
 *
 * A pendulum with a ferromagnetic bob attracted to 3 magnets
 * placed below. The pendulum oscillates chaotically as it's pulled
 * toward different magnets. Path traces the chaotic trajectory.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MagneticPendulumPipeline {
  program: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;

// Forward declaration for distToSegment.
float distToSegment(vec2 p, vec2 a, vec2 b);

vec2 pendulumPos(float t) {
  // Three magnets at the corners of an equilateral triangle.
  vec2 magnets[3];
  magnets[0] = vec2(0.0, -0.3);
  magnets[1] = vec2(0.26, -0.15);
  magnets[2] = vec2(-0.26, -0.15);
  // The pendulum has a long arm with the bob at the end.
  // Combined influence from all magnets + gravity + initial conditions.
  // We use a slow oscillation around the center, modulated by magnet influence.
  float omega = 1.5;
  float phase = t * omega;
  // Magnets attract the bob.
  vec2 bobPos = vec2(
    0.3 * sin(phase) + 0.05 * sin(t * 0.7),
    -0.3 + 0.1 * cos(phase)
  );
  // Add influence from magnets (the bob is pulled toward them).
  for (int i = 0; i < 3; i++) {
    vec2 m = magnets[i];
    vec2 toBob = bobPos - m;
    float dist = length(toBob) + 0.1;
    bobPos += normalize(toBob) * 0.05 / (dist * dist);
  }
  return bobPos;
}

void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.y = -p.y;

  // Dark background.
  vec3 col = vec3(0.02, 0.04, 0.08);

  // Three magnets (drawn as red dots).
  vec2 magnets[3];
  magnets[0] = vec2(0.0, 0.3);
  magnets[1] = vec2(0.26, 0.15);
  magnets[2] = vec2(-0.26, 0.15);
  for (int i = 0; i < 3; i++) {
    float d = length(p - magnets[i]);
    col += vec3(1.0, 0.2, 0.3) * exp(-d * d * 2000.0) * 1.5;
    col += vec3(1.0, 0.4, 0.5) * exp(-d * d * 100.0) * 0.2;
  }

  // Anchor at top.
  float anchorDist = length(p - vec2(0.0, 0.5));
  col += vec3(0.8, 0.8, 0.9) * exp(-anchorDist * anchorDist * 5000.0);

  // Suspension string.
  vec2 currentPos = pendulumPos(uTime);
  float armDist = distToSegment(p, vec2(0.0, 0.5), currentPos);
  col += vec3(0.7, 0.7, 0.8) * exp(-armDist * armDist * 4000.0) * 0.6;

  // Trace the path.
  const int N_TRACE = 300;
  for (int i = 0; i < N_TRACE; i++) {
    float trailT = uTime - float(i) * 0.015;
    vec2 trailPos = pendulumPos(trailT);
    float d = length(p - trailPos);
    float fade = 1.0 - float(i) / float(N_TRACE);
    float glow = exp(-d * d * 1500.0) * fade;
    // Color: hue from green (recent) to magenta (older).
    float hue = float(i) / float(N_TRACE);
    vec3 pathColor = mix(
      vec3(0.95, 0.85, 0.30),
      vec3(0.85, 0.30, 0.95),
      hue
    );
    col += pathColor * glow * (1.0 + mid * 0.5);
  }

  // Bob.
  float bobDist = length(p - currentPos);
  col += vec3(1.0, 1.0, 0.85) * exp(-bobDist * bobDist * 4000.0) * 2.0;
  col += vec3(0.95, 0.85, 0.55) * exp(-bobDist * bobDist * 200.0) * 0.4;

  float vig = 1.0 - dot(p, p) * 0.2;
  col *= clamp(vig, 0.0, 1.0);
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}

float distToSegment(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}
`;

export function computeMagneticPendulumBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeMagneticPendulumRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createMagneticPendulumPipeline(gl: WebGL2RenderingContext): MagneticPendulumPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawMagneticPendulum(
  gl: WebGL2RenderingContext,
  pipe: MagneticPendulumPipeline,
  width: number,
  height: number,
  t: number,
): void {
  if (!pipe.program) return;
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
